import { CardContent } from '@mui/material'
import { Spacer, Item } from '../../styles'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import Feedback from '../../../../../components/Feedback'
import FeedbackError from '../../../../../components/DataTable/Feedback'
import { bffService } from '../../../../../services/utils/axios'
import { StyledView, StyledTypography } from '../common/styles'
import ContractStatusSelector from '../common/ContractStatusSelector'
import React from 'react'
import { formatBooleanValue } from '../../../../../services/utils/formatters'

/**
 * A component that displays and manages additional contract information for producers
 *
 * @param {object} contract - Contains the producer's contract data from the new API
 * @param {string} contract.uuid - Unique identifier for the contract
 * @param {string} contract.contractNumber - User's contract number (required for ACTIVE status)
 * @param {string} contract.contractStatus - Current status of the contract (ACTIVE/INACTIVE/IN_PROGRESS)
 * @param {boolean} contract.isAlreadyClientOfUtility - Whether customer has an operator relationship
 * @param {string} contract.utilityContractNumber - Operator contract number (if applicable)
 * @param {boolean} contract.isSalesTaxLiable - Sales tax liability status
 * @param {boolean} contract.isTermsAndConditionsAccepted - Whether user has accepted terms and conditions
 * @param {boolean} contract.isMarketingInformationConsentGiven - Whether user has opted in for marketing communications
 */
function ProducerOtherData({ contract }) {
  const { t } = useTranslation()
  const [status, setStatus] = useState(contract?.contractStatus)
  const [tempStatus, setTempStatus] = useState(status)
  const [showIcons, setShowIcons] = useState(false)
  const [successFeedback, setSuccessFeedback] = useState(false)
  const [errorFeedback, setErrorFeedback] = useState(false)
  const [showContractStatusError, setShowContractStatusError] = useState(false)

  useEffect(() => {
    if (contract?.contractStatus) {
      setStatus(contract.contractStatus)
    }
  }, [contract?.contractStatus])

  const handleStatusChange = (event) => {
    setTempStatus(event.target.value)
    setStatus(event.target.value)
    setShowIcons(true)
  }

  const handleClose = () => {
    setShowIcons(false)
    setTempStatus(status)
    setStatus(contract?.contractStatus)
  }

  // API Call for updating contract status using PATCH /contracts/{uuid}
  const handleUpdateStatus = async () => {
    if (!contract?.contractNumber && tempStatus === 'ACTIVE') {
      return setShowContractStatusError(true)
    }

    try {
      const requestBody = {
        contractStatus: tempStatus,
      }

      const response = await bffService.patch(
        `/contracts/${contract.uuid}`,
        requestBody
      )

      if (response.data) {
        setSuccessFeedback(true)
        setStatus(tempStatus)
        setShowIcons(false)
      }
    } catch (error) {
      console.error('Error updating contract status:', error)
      setErrorFeedback(true)
    }
  }

  return (
    <>
      <StyledView>
        <CardContent>
          <StyledTypography fontWeight="bold" variant="h4" gutterBottom>
            {t('LBLOtherData')}
          </StyledTypography>
          <Spacer mb={4} />

          <StyledTypography variant="body2" component="div" gutterBottom>
            {/* Contract Type field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLContractType')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.energyRole === 'PRODUCER'
                  ? t('LBLProducer')
                  : t('LBLConsumer')}
              </StyledTypography>
            </Item>
            <div style={{ height: '5px' }}></div>

            {/* StadtWerkeKunden field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLStadtWerkeKundenAUW')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {formatBooleanValue(contract?.isAlreadyClientOfUtility, t)}
              </StyledTypography>
            </Item>
            <div style={{ height: '5px' }}></div>

            {/* StadtWerkeKundenNr field - only shown if relevant */}
            {contract?.utilityContractNumber && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLStadtWerkeKundenNr')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {contract?.utilityContractNumber}
                  </StyledTypography>
                </Item>
                <div style={{ height: '5px' }}></div>
              </>
            )}

            {/* Contract Status field with custom selector */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLUserStatus')}
              </StyledTypography>
              <ContractStatusSelector
                status={status}
                contract={contract}
                onStatusChange={handleStatusChange}
                showIcons={showIcons}
                onUpdate={handleUpdateStatus}
                onCancel={handleClose}
              />
            </Item>
            <div style={{ height: '5px' }}></div>

            {/* Tax Details field - only shown if available */}
            {contract?.isSalesTaxLiable !== undefined && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLSalesTaxLiability')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {formatBooleanValue(contract?.isSalesTaxLiable, t)}
                  </StyledTypography>
                </Item>
                <div style={{ height: '5px' }}></div>
              </>
            )}

            {/* AGB field */}
            {contract?.isTermsAndConditionsAccepted !== undefined && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLAgb')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {formatBooleanValue(
                      contract?.isTermsAndConditionsAccepted,
                      t
                    )}
                  </StyledTypography>
                </Item>
                <div style={{ height: '5px' }}></div>
              </>
            )}

            {/* Marketing Information field */}
            {contract?.isMarketingInformationConsentGiven !== undefined && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('UserDetailsMarketingInformation')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {formatBooleanValue(
                      contract?.isMarketingInformationConsentGiven,
                      t
                    )}
                  </StyledTypography>
                </Item>
                <div style={{ height: '5px' }}></div>
              </>
            )}
          </StyledTypography>
        </CardContent>
      </StyledView>

      {/* Success Feedback */}
      <Feedback
        open={successFeedback}
        title={t('LBLStatusUpdatedTitle')}
        message={
          <StyledTypography align="center">
            {t('LBLStatusUpdated')}.
          </StyledTypography>
        }
        handleClose={() => setSuccessFeedback(false)}
      />

      {/* Error Feedback */}
      <FeedbackError
        open={errorFeedback}
        title={t('LBLFailure')}
        message={
          <StyledTypography align="center">
            {t('LBLSomethingwentWrong')}
          </StyledTypography>
        }
        severity="warning"
        handleClose={() => {
          setErrorFeedback(false)
        }}
      />

      {/* Contract Number Error */}
      <FeedbackError
        open={showContractStatusError}
        title={t('LBLWarning')}
        message={
          <StyledTypography align="center">
            {t('LBLDisabledMessage')}
          </StyledTypography>
        }
        severity="warning"
        handleClose={() => {
          setShowContractStatusError(false)
          setShowIcons(false)
        }}
      />
    </>
  )
}

export default ProducerOtherData
